/**
 * Client for the Smoke-Test release-train API (/api/smoketest).
 *
 * The whole site is private, so every request needs a session; the token (shared
 * with Go-Live / Release Train, localStorage) is sent as a Bearer header — which
 * is what makes it work inside the ADO iframe where third-party cookies are
 * blocked. Every mutation returns the updated train, so callers setState from the
 * response.
 */
import type { Env, Person, Train, TrainSummary } from "@/types/smoketest"

const BASE = "/api/smoketest"
const TOKEN_KEY = "golive-token"

const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string>) }
  if (init?.body) headers["Content-Type"] = "application/json"
  if (token) headers["Authorization"] = `Bearer ${token}`
  const res = await fetch(`${BASE}${path}`, { credentials: "include", ...init, headers })
  if (!res.ok) {
    let detail = res.statusText
    try {
      detail = (await res.json()).detail ?? detail
    } catch {
      /* ignore */
    }
    const err = new Error(detail) as Error & { status: number }
    err.status = res.status
    throw err
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T)
}

const body = (data: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(data) })

/** Adding an item: `envs` may hold one or both — the server creates one item per
 *  env atomically, so ticking Staging + Prod never leaves a half-added pair. */
export interface NewItem {
  envs: Env[]
  title: string
  note?: string
  assignees?: string[]
  isTemplate?: boolean
}

export interface ItemPatch {
  title?: string
  note?: string
  env?: Env
  assignees?: string[]
  isTemplate?: boolean
  done?: boolean
}

export const smoketest = {
  people: () => req<Person[]>("/people"),
  list: () => req<TrainSummary[]>("/trains"),
  get: (id: number) => req<Train>(`/trains/${id}`),
  create: (version: string) => req<Train>("/trains", body({ version })),
  remove: (id: number) => req<{ ok: boolean }>(`/trains/${id}`, { method: "DELETE" }),
  addItem: (id: number, item: NewItem) => req<Train>(`/trains/${id}/items`, body(item)),
  patchItem: (id: number, itemId: string, patch: ItemPatch) =>
    req<Train>(`/trains/${id}/items/${itemId}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteItem: (id: number, itemId: string) =>
    req<Train>(`/trains/${id}/items/${itemId}`, { method: "DELETE" }),
}
